import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Phone, PhoneOff, Video, CalendarClock } from "lucide-react";
import GroupCallDialog from "@/components/GroupCallDialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RING_TIMEOUT_MS = 45000;

function useRingtone(active) {
  useEffect(() => {
    if (!active) return;
    let ctx, timer, stopped = false;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      ctx = new Ctx();
      const burst = () => {
        if (stopped) return;
        [0, 0.35].forEach(off => {
          const osc = ctx.createOscillator(); const gain = ctx.createGain();
          osc.type = "sine"; osc.frequency.value = 880;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime + off);
          gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + off + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + off + 0.3);
          osc.connect(gain).connect(ctx.destination);
          osc.start(ctx.currentTime + off); osc.stop(ctx.currentTime + off + 0.32);
        });
        timer = setTimeout(burst, 2000);
      };
      burst();
      if (navigator.vibrate) navigator.vibrate([400, 200, 400]);
    } catch { /* autoplay blocked */ }
    return () => { stopped = true; clearTimeout(timer); ctx?.close?.(); };
  }, [active]);
}

export default function MeetingInviteListener() {
  const { user } = useAuth();
  const [incoming, setIncoming] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const seenRef = useRef(new Set());
  const isCall = !!incoming && !incoming.is_scheduled;
  useRingtone(isCall);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/notifications?unread=true&limit=5`);
        const items = Array.isArray(r.data) ? r.data : (r.data?.notifications || r.data?.items || []);
        for (const n of items) {
          if (n.type !== "meeting_invite" || seenRef.current.has(n.notif_id)) continue;
          seenRef.current.add(n.notif_id);
          const meta = n.data || n.meta || {};
          const ageMs = Date.now() - new Date(n.created_at).getTime();
          if (meta.meeting_id && (meta.is_scheduled || ageMs < RING_TIMEOUT_MS)) {
            if (!cancelled) setIncoming({ notif_id: n.notif_id, ...meta });
            break;
          }
        }
      } catch { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  useEffect(() => {
    if (!isCall) return;
    const t = setTimeout(() => setIncoming(null), RING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isCall, incoming?.meeting_id]);

  const markRead = (id) => axios.post(`${API}/notifications/${id}/read`).catch(() => {});
  const accept = () => { markRead(incoming.notif_id); setCurrentMeeting(incoming); setIncoming(null); setCallOpen(true); };
  const decline = async () => {
    markRead(incoming.notif_id);
    if (!incoming.is_scheduled) axios.post(`${API}/meetings/${incoming.meeting_id}/decline`).catch(() => {});
    setIncoming(null);
  };

  if (!user) return null;
  const audioOnly = !!incoming?.audio_only;
  return (
    <>
      <Dialog open={!!incoming} onOpenChange={(v) => !v && decline()}>
        <DialogContent className="max-w-sm text-center border-0 bg-slate-900 text-white" data-testid="meeting-invite-dialog">
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="relative">
              <span className={`absolute inset-0 rounded-full ${isCall ? "animate-ping bg-emerald-500/40" : ""}`} />
              <Avatar className="w-24 h-24 ring-4 ring-white/20 relative" data-testid="incoming-call-avatar">
                <AvatarImage src={incoming?.host_picture} />
                <AvatarFallback className="text-2xl bg-slate-700 text-white">{incoming?.host_name?.[0] || "?"}</AvatarFallback>
              </Avatar>
            </div>
            <div className="text-xl font-bold" data-testid="incoming-call-name">{incoming?.host_name || "Alguém"}</div>
            <div className="text-sm text-white/70 flex items-center gap-1.5" data-testid="incoming-call-kind">
              {incoming?.is_scheduled ? <><CalendarClock className="w-4 h-4" /> Convite para reunião agendada</>
                : audioOnly ? <><Phone className="w-4 h-4" /> Chamada de voz recebida...</> : <><Video className="w-4 h-4" /> Chamada de vídeo recebida...</>}
            </div>
            {incoming?.title && incoming.kind !== "call" && <div className="text-xs text-white/50">{incoming.title}</div>}
            <div className="flex gap-8 mt-4">
              <div className="flex flex-col items-center gap-1">
                <Button data-testid="meeting-invite-decline" onClick={decline} className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700" aria-label="Recusar"><PhoneOff className="w-6 h-6" /></Button>
                <span className="text-[11px] text-white/70">Recusar</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Button data-testid="meeting-invite-accept" onClick={accept} className="rounded-full w-14 h-14 bg-emerald-600 hover:bg-emerald-700 animate-bounce" aria-label="Atender">
                  {audioOnly ? <Phone className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                </Button>
                <span className="text-[11px] text-white/70">{incoming?.is_scheduled ? "Entrar" : "Atender"}</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <GroupCallDialog
        open={callOpen}
        onOpenChange={(v) => { setCallOpen(v); if (!v) setCurrentMeeting(null); }}
        meetingId={currentMeeting?.meeting_id ?? null}
        audioOnly={!!currentMeeting?.audio_only}
        title={currentMeeting?.kind === "call" ? `${currentMeeting?.audio_only ? "Chamada de voz" : "Chamada de vídeo"} com ${currentMeeting?.host_name || ""}` : (currentMeeting?.title ?? "Chamada")}
      />
    </>
  );
}
